import React, { useState } from 'react';
import {
  Button,
  Avatar,
  Box,
  CircularProgress,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Fade,
  Snackbar,
  Alert,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Google, Apple, Facebook, Person, ExitToApp } from '@mui/icons-material'; // Added icons for menu items

/**
 * UserMenu component for authentication UI.
 * - Shows login button if not authenticated.
 * - Shows user avatar which opens a menu (Profile, Logout) if authenticated.
 * - Handles loading and error states with improved styling.
 */
export default function UserMenu({ onMenuClick, isMobileDrawer = false }) {
  const { user, loading, error, login, logout, notification, setNotification } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    if (onMenuClick) {
      onMenuClick();
    }
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const handleNotificationClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobileDrawer ? 'center' : 'flex-end',
          p: isMobileDrawer ? 2 : 1,
          bgcolor: isMobileDrawer ? 'background.paper' : 'transparent',
          borderRadius: 1,
          width: isMobileDrawer ? '100%' : 'auto',
        }}
      >
        <CircularProgress size={24} color="primary" />
      </Box>
    );
  }

  if (user) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobileDrawer ? 'center' : 'flex-end',
          ml: isMobileDrawer ? 0 : 2,
          width: isMobileDrawer ? '100%' : 'auto',
          p: isMobileDrawer ? 1 : 0,
        }}
      >
        <Tooltip title={user.displayName || user.email || 'User Account'} arrow>
          <IconButton
            onClick={handleMenuOpen}
            size="small"
            sx={{
              p: 0,
              transition: 'transform 0.2s ease-in-out',
              '&:hover': { transform: 'scale(1.1)' },
            }}
            aria-controls={open ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            aria-label="Open user account menu"
          >
            {user.photo ? (
              <Avatar
                src={user.photo}
                alt={user.displayName || user.email || 'User Avatar'}
                sx={{
                  width: 36,
                  height: 36,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
              </Avatar>
            )}
          </IconButton>
        </Tooltip>
        <Menu
          id="user-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          TransitionComponent={Fade}
          MenuListProps={{
            'aria-labelledby': 'user-menu-button',
            sx: { py: 0.5 },
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            elevation: 3,
            sx: {
              borderRadius: 2,
              minWidth: 160,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              mt: 1,
            },
          }}
        >
          <MenuItem
            component={Link}
            to="/profile"
            onClick={handleMenuClose}
            sx={{
              py: 1,
              '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' },
            }}
          >
            <Person sx={{ mr: 1, fontSize: 20 }} />
            Edit Profile
          </MenuItem>
          <MenuItem
            onClick={handleLogout}
            sx={{
              py: 1,
              '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' },
            }}
          >
            <ExitToApp sx={{ mr: 1, fontSize: 20 }} />
            Logout
          </MenuItem>
        </Menu>
      </Box>
    );
  }

  // Logged out state
  return (
    <>
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleNotificationClose} severity="success" sx={{ width: '100%' }}>
          {notification}
        </Alert>
      </Snackbar>
    <Box
      sx={{
        ml: isMobileDrawer ? 0 : 2,
        width: isMobileDrawer ? '100%' : 'auto',
        p: isMobileDrawer ? 1 : 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: isMobileDrawer ? 'center' : 'flex-end',
        gap: 1,
      }}
    >
      <Button
        color="primary"
        variant={isMobileDrawer ? 'contained' : 'outlined'}
        size="medium"
        onClick={handleMenuOpen}
        aria-label="Open login options"
        fullWidth={isMobileDrawer}
        sx={{
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 'medium',
          px: 3,
          py: 0.75,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            bgcolor: isMobileDrawer ? 'primary.dark' : 'primary.light',
            transform: 'translateY(-1px)',
          },
        }}
      >
        Sign In
      </Button>
      <Menu
        id="login-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        TransitionComponent={Fade}
        MenuListProps={{
          'aria-labelledby': 'login-button',
          sx: { py: 0.5 },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            minWidth: 160,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            mt: 1,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            login('google');
            handleMenuClose();
          }}
          sx={{
            py: 1,
            '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' },
          }}
        >
          <Google sx={{ mr: 1, fontSize: 20 }} />
          Sign in with Google
        </MenuItem>
        <MenuItem
          onClick={() => {
            login('apple');
            handleMenuClose();
          }}
          sx={{
            py: 1,
            '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' },
          }}
        >
          <Apple sx={{ mr: 1, fontSize: 20 }} />
          Sign in with Apple
        </MenuItem>
        {/* <MenuItem
          onClick={() => {
            login('facebook');
            handleMenuClose();
          }}
          sx={{
            py: 1,
            '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' },
          }}
        >
          <Facebook sx={{ mr: 1, fontSize: 20 }} />
          Sign in with Facebook
        </MenuItem> */}
      </Menu>
      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{
            mt: 1,
            px: 2,
            py: 0.5,
            bgcolor: 'error.light',
            borderRadius: 1,
            textAlign: isMobileDrawer ? 'center' : 'left',
            maxWidth: isMobileDrawer ? '100%' : 200,
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
    </>
  );
}